import { Component, inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { FormGroup } from '@ngneat/reactive-forms';
import { PaginatorModule } from 'primeng/paginator';
import { CheckboxModule } from 'primeng/checkbox';

import { CardModule } from 'primeng/card';
import { AbstractFormComponent, EbfFormsModule, SelectItem } from '@ebf-libs/sdk/platform/forms';
import { EbfFormsValidationModule, validateFormFields } from '@ebf-libs/sdk/platform/forms-validation';
import { CompleteRegistrationForm } from '@ebf/core/user/domain';
import { CountryComponent } from '@ebf/shared/country/view/country/country.component';
import { UsersFacadeService } from '@ebf/features/users/services/users-facade.service';
import { ApiError } from '@ebf-libs/sdk/platform/api';
import { ChampionshipDto } from '@ebf/features/championships/domain';
import { buildActiveChampionshipUrl, hasPathname } from '@ebf-libs/sdk/platform/utils';
import { ChampionshipsApiService } from '@ebf/features/championships/services/championships-api.service';

import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'ebf-complete-registration',
  templateUrl: './complete-registration.component.html',
  styleUrls: ['./complete-registration.component.scss'],
  standalone: true,
  imports: [
    CardModule,
    EbfFormsModule,
    EbfFormsValidationModule,
    PaginatorModule,
    ReactiveFormsModule,
    CountryComponent,
    NgIf,
    AsyncPipe,
    CheckboxModule,
  ],
})
export class CompleteRegistrationComponent
  extends AbstractFormComponent<CompleteRegistrationForm>
  implements OnInit
{
  public countries$: Observable<SelectItem[]>;
  public championship?: ChampionshipDto;
  public isCreateModel: boolean;
  public backUrl?: string;
  public form: FormGroup<CompleteRegistrationForm>;
  private readonly usersFacadeService: UsersFacadeService = inject(UsersFacadeService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly championshipsApiService: ChampionshipsApiService = inject(ChampionshipsApiService);

  public ngOnInit() {
    this.isCreateModel = this.activatedRoute.snapshot.data.isCreateModel;
    const user = this.usersFacadeService.getUserSnapshot();
    const { backUrl } = this.activatedRoute.snapshot.queryParams;

    this.backUrl = backUrl ? decodeURIComponent(backUrl) : null;
    this.countries$ = this.usersFacadeService.getCountries().pipe(tap(console.log));
    this.form = this.formBuilder.group({
      firstName: [user?.firstName ?? '', [Validators.required]],
      lastName: [user?.lastName ?? '', [Validators.required]],
      countryId: ['', [Validators.required]],
      phoneNumber: [user?.phoneNumber ?? ''],
      email: [{ value: user?.email ?? '', disabled: !this.isCreateModel }, [Validators.required]],
    });
    this.getChampionship();
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      validateFormFields(this.form);

      return;
    }

    const backUrl = decodeURIComponent(this.activatedRoute.snapshot.queryParams.backUrl);
    const request = this.isCreateModel
      ? this.usersFacadeService.createModelAccount(this.form.getRawValue(), this.championship.uuid)
      : this.usersFacadeService.completeRegistration(this.form.getRawValue());

    this.setRequestPending(true);
    this.subSink.sink = request
      .pipe(
        finalize(() => this.setRequestPending(false)),
        switchMap(() =>
          hasPathname(backUrl) ? of(null) : this.championshipsApiService.getCurrentChampionship(),
        ),
      )
      .subscribe({
        next: championship => {
          this.isSuccess = true;
          const activeChampionshipUrl = buildActiveChampionshipUrl(championship);
          const { currentChampionshipRedirectEnabled } = environment;

          if (activeChampionshipUrl && currentChampionshipRedirectEnabled) {
            window.location.href = `${window.location.origin}${activeChampionshipUrl}`;
          } else {
            window.location.href = backUrl;
          }
        },
        error: (apiError: ApiError) => this.displayErrorMessages(apiError),
      });
  }

  public getSelectedCountryData(countries: SelectItem[]): SelectItem {
    return countries?.find((country: SelectItem): boolean => country.value === this.form.value.countryId);
  }

  private getChampionshipUuidFromUrl(): string | null {
    if (!this.backUrl) {
      return null;
    }

    const searchParams = new URLSearchParams(this.backUrl);
    const championshipUuidFromQuery: string | undefined = searchParams.get('championshipUuid');

    if (championshipUuidFromQuery) {
      return championshipUuidFromQuery;
    }

    const countryUuidRegex = /countries\/([\da-fA-F-]+)/;
    const champUuidRegex = /championships\/([\da-fA-F-]+)/;
    const countryChampUuid = this.backUrl.match(countryUuidRegex)?.[1];
    const champUuid = this.backUrl.match(champUuidRegex)?.[1];

    return countryChampUuid || champUuid;
  }

  private getChampionship(): void {
    const championshipUuid: string | undefined = this.getChampionshipUuidFromUrl();

    if (!championshipUuid) {
      return;
    }

    this.setRequestPending(true);
    this.subSink.sink = this.usersFacadeService.getChampionshipById(championshipUuid).subscribe(response => {
      this.championship = response;
      this.setRequestPending(false);
    });
  }
}
